import {
  Client,
  ReconnectionTimeMode,
  type IMessage,
} from '@stomp/stompjs';
import { CAMUNDA_AUTH_EXPIRED_EVENT } from '@/lib/camunda/base-service';
import { syncAuthStateFromSession } from '@/lib/auth-client';
import {
  getAuthStateSnapshot,
  subscribeAuthState,
} from '@/lib/auth-store';
import { ENV_CONFIG } from '@/lib/env-config';
import { TaskReconciliationScheduler } from './reconciliation-scheduler';
import {
  calculateTaskRealtimeRenewalDelay,
  MAX_TOKEN_RENEWAL_BACKOFF_MS,
  MIN_TOKEN_RENEWAL_BACKOFF_MS,
} from './lifecycle';
import {
  parseTaskRealtimeEnvelopePayload,
  TASK_REALTIME_SUBSCRIPTION,
  type TaskRealtimeEnvelope,
} from './types';

const REALTIME_RECONCILIATION_DEBOUNCE_MS = 250;
const REALTIME_RECONCILIATION_JITTER_MS = 750;
const REALTIME_RECONCILIATION_MIN_INTERVAL_MS = 2_000;
const REALTIME_ERROR_MESSAGE = 'No se pudo sincronizar en tiempo real';
const DEBUG_TASK_REALTIME =
  import.meta.env.DEV || import.meta.env.VITE_DEBUG_TASK_REALTIME === 'true';

interface TaskRealtimeConnectionState {
  connected: boolean;
  error: string | null;
}

interface TaskRealtimeCallbacks {
  onStatusChange: (state: TaskRealtimeConnectionState) => void;
  onReconcile: (reasons: string[]) => void | Promise<void>;
  onEvent?: (event: TaskRealtimeEnvelope) => void | Promise<void>;
}

type TaskRealtimeEventListener = (event: TaskRealtimeEnvelope) => void;
type TaskRealtimeCredential = Pick<
  ReturnType<typeof getAuthStateSnapshot>,
  'accessToken' | 'expiresAt'
>;

interface TaskRealtimeAuthRefreshScope {
  activationGeneration: number;
  refreshGeneration: number;
  client: Client | null;
  credential: TaskRealtimeCredential;
}

interface TaskRealtimeAuthRefresh extends TaskRealtimeAuthRefreshScope {
  promise: Promise<void>;
}

function readCredential(): TaskRealtimeCredential {
  const { accessToken, expiresAt } = getAuthStateSnapshot();
  return { accessToken, expiresAt };
}

function isSameCredential(
  left: TaskRealtimeCredential,
  right: TaskRealtimeCredential,
): boolean {
  return left.accessToken === right.accessToken && left.expiresAt === right.expiresAt;
}

function notifyAuthExpired(reason: string): void {
  const browserWindow = globalThis.window;
  if (browserWindow === undefined) {
    return;
  }

  browserWindow.dispatchEvent(
    new CustomEvent(CAMUNDA_AUTH_EXPIRED_EVENT, {
      detail: { reason },
    }),
  );
}

class TaskRealtimeClientManager {
  private client: Client | null = null;
  private callbacks: TaskRealtimeCallbacks | null = null;
  private activeOwner: symbol | null = null;
  private deactivationTimer: ReturnType<typeof setTimeout> | null = null;
  private renewalTimer: ReturnType<typeof setTimeout> | null = null;
  private renewalBackoffMs = MIN_TOKEN_RENEWAL_BACKOFF_MS;
  private unsubscribeAuthState: (() => void) | null = null;
  private visibilityHandler: (() => void) | null = null;
  private shouldRun = false;
  private isDeactivating = false;
  private activationGeneration = 0;
  private authRefreshGeneration = 0;
  private authRefresh: TaskRealtimeAuthRefresh | null = null;
  private blockedCredential: TaskRealtimeCredential | null = null;
  private transportCredential: TaskRealtimeCredential | null = null;
  private state: TaskRealtimeConnectionState = { connected: false, error: null };
  private readonly eventListeners = new Set<TaskRealtimeEventListener>();
  private readonly reconciliationScheduler = new TaskReconciliationScheduler(
    (reasons) => this.runReconciliation(reasons),
    {
      delayMs: REALTIME_RECONCILIATION_DEBOUNCE_MS,
      jitterMs: REALTIME_RECONCILIATION_JITTER_MS,
      minIntervalMs: REALTIME_RECONCILIATION_MIN_INTERVAL_MS,
    },
  );

  activate(callbacks: TaskRealtimeCallbacks): () => void {
    if (globalThis.window === undefined) {
      return () => undefined;
    }

    const owner = Symbol('task-realtime-owner');
    const wasRunning = this.shouldRun;
    this.activeOwner = owner;
    this.callbacks = callbacks;

    if (this.deactivationTimer !== null) {
      clearTimeout(this.deactivationTimer);
      this.deactivationTimer = null;
    }

    this.shouldRun = true;
    if (!wasRunning) {
      this.activationGeneration += 1;
      if (!this.isDeactivating) {
        this.subscribeToAuthState();
        this.subscribeToVisibility();
        this.emitState(this.state);
        this.createClient();
      }
    } else {
      this.emitState(this.state);
    }

    let released = false;
    return () => {
      if (released) {
        return;
      }
      released = true;
      if (this.activeOwner !== owner) {
        return;
      }
      this.activeOwner = null;
      this.callbacks = null;
      if (this.deactivationTimer !== null) {
        return;
      }

      this.deactivationTimer = setTimeout(() => {
        this.deactivationTimer = null;
        if (this.activeOwner === null) {
          void this.deactivateNow();
        }
      }, 0);
    };
  }

  subscribe(listener: TaskRealtimeEventListener): () => void {
    this.eventListeners.add(listener);
    return () => this.eventListeners.delete(listener);
  }

  private createClient(): void {
    if (!this.shouldRun || this.blockedCredential || this.client) {
      return;
    }

    const client = new Client({
      webSocketFactory: () => new WebSocket(ENV_CONFIG.getTaskWebSocketUrl()),
      connectHeaders: {},
      heartbeatIncoming: 10_000,
      heartbeatOutgoing: 10_000,
      connectionTimeout: 10_000,
      reconnectDelay: 1_000,
      maxReconnectDelay: 30_000,
      reconnectTimeMode: ReconnectionTimeMode.EXPONENTIAL,
      debug: (message) => {
        if (DEBUG_TASK_REALTIME && /connected|disconnected|error/i.test(message)) {
          console.debug('[task-realtime] STOMP lifecycle');
        }
      },
    });

    client.beforeConnect = async () => {
      await this.prepareConnection(client);
    };
    client.onConnect = () => {
      if (this.client !== client || !this.shouldRun) {
        void client.deactivate({ force: true });
        return;
      }

      this.emitState({ connected: true, error: null });
      client.subscribe(TASK_REALTIME_SUBSCRIPTION, (message) => {
        this.handleMessage(message);
      });
      this.reconciliationScheduler.schedule('connect');
    };
    client.onWebSocketClose = () => {
      if (this.client !== client || !this.shouldRun) {
        return;
      }
      this.transportCredential = null;
      this.emitState({
        connected: false,
        error: this.blockedCredential ? REALTIME_ERROR_MESSAGE : null,
      });
    };
    client.onWebSocketError = () => {
      if (this.client === client && this.shouldRun) {
        this.emitState({ connected: false, error: REALTIME_ERROR_MESSAGE });
      }
    };
    client.onStompError = () => {
      if (this.client === client && this.shouldRun) {
        this.emitState({ connected: false, error: REALTIME_ERROR_MESSAGE });
      }
    };
    client.onDisconnect = () => {
      if (this.client === client && !this.shouldRun) {
        this.emitState({ connected: false, error: null });
      }
    };

    this.client = client;
    try {
      client.activate();
    } catch {
      this.emitState({ connected: false, error: REALTIME_ERROR_MESSAGE });
    }
  }

  private async prepareConnection(client: Client): Promise<void> {
    if (!this.shouldRun || this.blockedCredential) {
      await client.deactivate({ force: true });
      return;
    }

    const previousCredential = readCredential();
    try {
      const sessionRecovered = await syncAuthStateFromSession();
      if (!this.shouldRun || this.client !== client) {
        await client.deactivate({ force: true });
        return;
      }
      if (!sessionRecovered) {
        this.handleAuthFailure(
          'task_realtime_auth_expired',
          client,
          previousCredential,
        );
        return;
      }

      const credential = readCredential();
      if (!credential.accessToken) {
        this.handleAuthFailure(
          'task_realtime_missing_access_token',
          client,
          previousCredential,
        );
        return;
      }

      client.connectHeaders = {
        Authorization: `Bearer ${credential.accessToken}`,
      };
      this.transportCredential = credential;
      this.scheduleTokenRenewal(credential.expiresAt);
    } catch {
      this.handleAuthFailure(
        'task_realtime_auth_refresh_failed',
        client,
        previousCredential,
      );
    }
  }

  private handleMessage(message: IMessage): void {
    const envelope = parseTaskRealtimeEnvelopePayload(message.body);
    if (!envelope) {
      this.emitState({ connected: true, error: 'Evento de sincronizacion no valido' });
      return;
    }

    this.emitState({ connected: true, error: null });
    if (envelope.type === 'TASKS_INVALIDATED') {
      this.reconciliationScheduler.schedule('access-change');
    }
    try {
      const pendingEvent = this.callbacks?.onEvent?.(envelope);
      if (pendingEvent) {
        void pendingEvent.catch(() => {
          this.emitState({ connected: true, error: REALTIME_ERROR_MESSAGE });
        });
      }
    } catch {
      this.emitState({ connected: true, error: REALTIME_ERROR_MESSAGE });
    }
    this.eventListeners.forEach((listener) => {
      try {
        listener(envelope);
      } catch {
      }
    });
  }

  private async runReconciliation(reasons: string[]): Promise<void> {
    const callback = this.callbacks?.onReconcile;
    if (!callback) {
      return;
    }

    try {
      await callback(reasons);
    } catch {
      this.emitState({ connected: this.state.connected, error: REALTIME_ERROR_MESSAGE });
    }
  }

  private subscribeToAuthState(): void {
    this.unsubscribeAuthState?.();
    this.unsubscribeAuthState = subscribeAuthState((change) => {
      this.handleAuthStateChange(change.sessionConfirmed);
    });
    this.handleAuthStateChange();
  }

  private handleAuthStateChange(sessionConfirmed = false): void {
    const credential = readCredential();
    this.scheduleTokenRenewal(credential.expiresAt);

    if (this.blockedCredential) {
      if (
        !credential.accessToken ||
        (!sessionConfirmed && isSameCredential(credential, this.blockedCredential))
      ) {
        return;
      }

      this.blockedCredential = null;
      this.renewalBackoffMs = MIN_TOKEN_RENEWAL_BACKOFF_MS;
      this.createClient();
      return;
    }

    if (
      !this.shouldRun ||
      !this.client?.active ||
      !this.transportCredential
    ) {
      return;
    }

    if (!credential.accessToken) {
      this.handleAuthFailure(
        'task_realtime_missing_access_token',
        this.client,
        this.transportCredential,
      );
      return;
    }

    if (credential.accessToken !== this.transportCredential.accessToken) {
      this.emitState({ connected: false, error: null });
      this.client.forceDisconnect();
    }
  }

  private scheduleTokenRenewal(expiresAt: number | null): void {
    if (this.renewalTimer !== null) {
      clearTimeout(this.renewalTimer);
      this.renewalTimer = null;
    }

    if (!this.shouldRun || expiresAt === null) {
      return;
    }

    const delayMs = calculateTaskRealtimeRenewalDelay(
      expiresAt,
      Date.now(),
      this.renewalBackoffMs,
    );
    const activationGeneration = this.activationGeneration;
    const client = this.client;
    const credential = readCredential();
    const renewalTimer = setTimeout(() => {
      if (this.renewalTimer !== renewalTimer) {
        return;
      }
      this.renewalTimer = null;
      void this.refreshSessionBeforeExpiry(
        activationGeneration,
        client,
        credential,
      );
    }, delayMs);
    this.renewalTimer = renewalTimer;
  }

  private async refreshSessionBeforeExpiry(
    activationGeneration = this.activationGeneration,
    client = this.client,
    credential = readCredential(),
  ): Promise<void> {
    const refreshScope: TaskRealtimeAuthRefreshScope = {
      activationGeneration,
      refreshGeneration: this.authRefreshGeneration + 1,
      client,
      credential,
    };
    if (!this.isCurrentActivation(refreshScope)) {
      return;
    }

    if (this.isSameAuthRefreshScope(this.authRefresh, refreshScope)) {
      await this.authRefresh.promise;
      return;
    }

    const authRefresh: TaskRealtimeAuthRefresh = {
      ...refreshScope,
      promise: Promise.resolve(),
    };
    this.authRefreshGeneration = refreshScope.refreshGeneration;
    this.authRefresh = authRefresh;
    authRefresh.promise = this.performAuthRefresh(refreshScope);
    try {
      await authRefresh.promise;
    } finally {
      if (this.authRefresh === authRefresh) {
        this.authRefresh = null;
      }
    }
  }

  private async performAuthRefresh(
    refreshScope: TaskRealtimeAuthRefreshScope,
  ): Promise<void> {
    try {
      const sessionRecovered = await syncAuthStateFromSession();
      if (!this.isCurrentAuthRefresh(refreshScope)) {
        return;
      }
      const credential = readCredential();
      if (!sessionRecovered || !credential.accessToken) {
        this.handleAuthFailure(
          'task_realtime_auth_refresh_failed',
          refreshScope.client,
          refreshScope.credential,
        );
        return;
      }

      if (isSameCredential(credential, refreshScope.credential)) {
        this.renewalBackoffMs = Math.min(
          this.renewalBackoffMs * 2,
          MAX_TOKEN_RENEWAL_BACKOFF_MS,
        );
      } else {
        this.renewalBackoffMs = MIN_TOKEN_RENEWAL_BACKOFF_MS;
      }
      this.scheduleTokenRenewal(credential.expiresAt);
    } catch {
      if (!this.isCurrentAuthRefresh(refreshScope)) {
        return;
      }
      this.handleAuthFailure(
        'task_realtime_auth_refresh_failed',
        refreshScope.client,
        refreshScope.credential,
      );
    }
  }

  private isCurrentAuthRefresh(
    refreshScope: TaskRealtimeAuthRefreshScope,
  ): boolean {
    return (
      this.isCurrentActivation(refreshScope) &&
      this.authRefresh?.refreshGeneration === refreshScope.refreshGeneration
    );
  }

  private isCurrentActivation(
    refreshScope: TaskRealtimeAuthRefreshScope,
  ): boolean {
    return (
      this.shouldRun &&
      this.activationGeneration === refreshScope.activationGeneration &&
      this.client === refreshScope.client
    );
  }

  private isSameAuthRefreshScope(
    refresh: TaskRealtimeAuthRefresh | null,
    refreshScope: TaskRealtimeAuthRefreshScope,
  ): refresh is TaskRealtimeAuthRefresh {
    return (
      refresh !== null &&
      refresh.activationGeneration === refreshScope.activationGeneration &&
      refresh.client === refreshScope.client &&
      isSameCredential(refresh.credential, refreshScope.credential)
    );
  }

  private handleAuthFailure(
    reason: string,
    failedClient: Client | null,
    blockedCredential: TaskRealtimeCredential,
  ): void {
    if (!this.shouldRun || (failedClient && this.client !== failedClient)) {
      if (failedClient?.active) {
        void failedClient.deactivate({ force: true });
      }
      return;
    }

    this.blockedCredential = blockedCredential;
    this.transportCredential = null;
    if (this.renewalTimer !== null) {
      clearTimeout(this.renewalTimer);
      this.renewalTimer = null;
    }
    this.emitState({ connected: false, error: REALTIME_ERROR_MESSAGE });
    notifyAuthExpired(reason);
    if (this.client === failedClient) {
      this.client = null;
    }
    if (failedClient?.active) {
      void failedClient.deactivate({ force: true });
    }
  }

  private subscribeToVisibility(): void {
    this.visibilityHandler = () => {
      if (document.visibilityState === 'visible' && this.shouldRun) {
        this.reconciliationScheduler.schedule('visibility');
      }
    };
    document.addEventListener('visibilitychange', this.visibilityHandler);
  }

  private async deactivateNow(): Promise<void> {
    if (this.activeOwner !== null) {
      return;
    }

    this.isDeactivating = true;
    this.shouldRun = false;
    this.activationGeneration += 1;
    this.unsubscribeAuthState?.();
    this.unsubscribeAuthState = null;
    if (this.visibilityHandler) {
      document.removeEventListener('visibilitychange', this.visibilityHandler);
      this.visibilityHandler = null;
    }
    if (this.renewalTimer !== null) {
      clearTimeout(this.renewalTimer);
      this.renewalTimer = null;
    }
    this.reconciliationScheduler.dispose();
    this.renewalBackoffMs = MIN_TOKEN_RENEWAL_BACKOFF_MS;
    this.authRefresh = null;
    this.transportCredential = null;
    this.blockedCredential = null;

    const client = this.client;
    this.client = null;
    this.emitState({ connected: false, error: null });
    try {
      if (client) {
        try {
          await client.deactivate({ force: true });
        } catch {
        }
      }
    } finally {
      this.isDeactivating = false;
    }

    if (this.activeOwner !== null) {
      this.shouldRun = true;
      this.subscribeToAuthState();
      this.subscribeToVisibility();
      this.emitState(this.state);
      this.createClient();
      return;
    }

    this.emitState({ connected: false, error: null });
  }

  private emitState(state: TaskRealtimeConnectionState): void {
    this.state = state;
    this.callbacks?.onStatusChange(state);
  }
}

export const taskRealtimeClient = new TaskRealtimeClientManager();

export function subscribeTaskRealtimeEvents(listener: TaskRealtimeEventListener): () => void {
  return taskRealtimeClient.subscribe(listener);
}
